import type { MetricXAxisKind } from '@/components/charts/generic/axisTypes';
import { CHARTJS_MAX_TICKS_LIMIT, seriesColorAt } from '@/components/charts/generic/constants';
import type { MetricPointMarker } from '@/components/charts/generic/seriesTypes';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import type { CompareCage, CompareMetric } from '@/domain/locationCompare';
import type { Locale } from '@/utils/localeFormatters';

/**
 * A cage's line colour: the series palette in the order the cages arrive, so a cage keeps its
 * colour while others are toggled. Past the palette's twelve colours it wraps around.
 */
export const cageColor = (cageId: number, cages: CompareCage[]): string =>
    seriesColorAt(
        Math.max(
            0,
            cages.findIndex((c) => c.id === cageId)
        )
    );

/**
 * A filled dot on every day: the cages are compared day by day, and a dot shows which days each
 * cage was actually measured on, where a bare line would bridge them all alike.
 */
const DAY_MARKER: MetricPointMarker = { radius: 3, hoverRadius: 4, fill: 'series' };

export interface BuildCompareChartSpecParams {
    cages: CompareCage[];
    metric: CompareMetric | undefined;
    visibleCageIds: Set<number>;
    locale: Locale;
    /** Hour keys are `YYYY-MM-DD HH:mm:ss`. Defaults to a day axis. */
    xAxisKind?: MetricXAxisKind;
    /** Overrides `metric.beginAtZero` when the depth axis should scale to the data. */
    beginAtZero?: boolean;
    yMin?: number;
    yMax?: number;
}

/**
 * The comparison as a MetricChart spec: one line per visible cage for the selected metric.
 * Days are taken from the metric's dataPoints so the x axis stays aligned with the time range
 * that actually has data, and a cage with no value on a day gets a gap there.
 *
 * The cage legend lives on the page (`CageLegend`), so no series has a legend entry here, and
 * values are formatted by the metric's own formatter on the axis and in the tooltip alike. Lines
 * are straight between days, so a curve cannot suggest a value no cage had, and the value axis
 * keeps the finer Chart.js scale, where a gap of a few hundred grams between cages still shows.
 */
export function buildCompareChartSpec(
    params: BuildCompareChartSpecParams
): MetricChartSpec<'line'> {
    const { cages, metric, visibleCageIds, locale } = params;
    const dataPoints = metric?.dataPoints ?? [];
    const formatValue = metric?.formatValue;

    return {
        kind: 'line',
        days: dataPoints.map((point) => point.day),
        ...(params.xAxisKind ? { xAxisKind: params.xAxisKind } : {}),
        series: cages
            .filter((cage) => visibleCageIds.has(cage.id))
            .map((cage) => ({
                key: `cage:${cage.id}`,
                label: cage.name,
                color: cageColor(cage.id, cages),
                showInLegend: false,
                marker: DAY_MARKER,
                tension: 0,
                data: dataPoints.map((point) => ({
                    day: point.day,
                    value: point.values[cage.id] ?? null,
                })),
            })),
        left: {
            label: '',
            format: (value) => (formatValue ? formatValue(value, locale) : String(value)),
            maxTicksLimit: CHARTJS_MAX_TICKS_LIMIT,
            beginAtZero: params.beginAtZero ?? metric?.beginAtZero ?? true,
            ...(params.yMin !== undefined ? { min: params.yMin } : {}),
            ...(params.yMax !== undefined ? { max: params.yMax } : {}),
            reverse: metric?.reverse ?? false,
        },
        locale,
    };
}
